import { createContext, useContext, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Info, CheckCircle2, X } from "lucide-react";

const DialogCtx = createContext(null);

/**
 * Replaces window.confirm / window.alert / window.prompt with branded in-app modals.
 * Usage:
 *   const { confirm, notify, prompt } = useDialog();
 *   if (!await confirm({ title: "Delete?", message: "...", danger: true })) return;
 *   await notify({ title: "Saved", variant: "ok" });
 *   const v = await prompt({ title: "Extend trial", message: "By how many days?", defaultValue: "30", inputType: "number" });
 *   if (v === null) return; // cancelled
 */
export function DialogProvider({ children }) {
  const [stack, setStack] = useState([]);

  const close = useCallback((id, result) => {
    setStack((s) => {
      const item = s.find((x) => x.id === id);
      if (item?.resolve) item.resolve(result);
      return s.filter((x) => x.id !== id);
    });
  }, []);

  const push = useCallback(
    (kind, opts) =>
      new Promise((resolve) => {
        const id = Math.random().toString(36).slice(2);
        setStack((s) => [...s, { id, kind, resolve, ...opts }]);
      }),
    []
  );

  const confirm = useCallback((opts) => push("confirm", opts), [push]);
  const notify = useCallback((opts) => push("notify", opts), [push]);
  const prompt = useCallback((opts) => push("prompt", opts), [push]);

  const value = useMemo(() => ({ confirm, notify, prompt }), [confirm, notify, prompt]);

  return (
    <DialogCtx.Provider value={value}>
      {children}
      {stack.map((d) => (
        <DialogShell key={d.id} d={d} onClose={(r) => close(d.id, r)} />
      ))}
    </DialogCtx.Provider>
  );
}

function DialogShell({ d, onClose }) {
  const isConfirm = d.kind === "confirm";
  const isPrompt = d.kind === "prompt";
  const [val, setVal] = useState(d.defaultValue ?? "");
  const variant = d.variant || (d.danger ? "err" : "info");
  const Icon =
    variant === "err" ? AlertTriangle : variant === "ok" ? CheckCircle2 : Info;
  const accent =
    variant === "err"
      ? "var(--vb-error)"
      : variant === "ok"
      ? "var(--vb-accent)"
      : "var(--vb-text-2)";

  // Cancel result: false for confirm, null for prompt, false otherwise.
  const cancelResult = isPrompt ? null : false;
  const confirmResult = isPrompt ? val : true;

  return createPortal(
    <div className="vb-modal-bg" style={{ zIndex: 12000 }} onClick={() => (isConfirm || isPrompt) && onClose(cancelResult)} data-testid="vb-dialog">
      <div className="vb-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-2">
          <div
            className="rounded-full p-2 shrink-0"
            style={{ background: variant === "err" ? "#fecaca" : variant === "ok" ? "#dcefd2" : "var(--vb-bg-2)" }}
          >
            <Icon size={18} color={accent} />
          </div>
          <div className="flex-1">
            <h2 style={{ color: accent, margin: 0, fontSize: 18 }}>{d.title || (isConfirm || isPrompt ? "Are you sure?" : "Notice")}</h2>
          </div>
          <button onClick={() => onClose(cancelResult)} className="vb-link" data-testid="dialog-x"><X size={18} /></button>
        </div>
        {d.message && <p className="text-sm opacity-85 mb-3 ml-12">{d.message}</p>}
        {d.body && <div className="ml-12 mb-3">{d.body}</div>}
        {isPrompt && (
          <div className="ml-12 mb-3">
            <input
              className="vb-input w-full"
              type={d.inputType || "text"}
              value={val}
              placeholder={d.placeholder || ""}
              onChange={(e) => setVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onClose(confirmResult); }}
              autoFocus
              data-testid="dialog-input"
            />
          </div>
        )}
        <div className="flex gap-2 justify-end mt-4">
          {(isConfirm || isPrompt) && (
            <button className="vb-btn ghost" onClick={() => onClose(cancelResult)} data-testid="dialog-cancel">
              {d.cancelLabel || "Cancel"}
            </button>
          )}
          <button
            className={`vb-btn ${d.danger ? "danger" : ""}`}
            onClick={() => onClose(confirmResult)}
            data-testid="dialog-confirm"
            autoFocus={!isPrompt}
          >
            {d.confirmLabel || (isConfirm || isPrompt ? "Confirm" : "OK")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export const useDialog = () => useContext(DialogCtx);
